import React from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import moment from "moment";
import {
    ModalHeader,
    ScrollableContent,
    ModalContent,
    ModalRow,
    ModalRowLabel,
    ModalRowValue,
    ModalSection,
    ModalSectionTitle,
} from "../Styles/InputStyles";
import ModalProfileHeaderWithoutImage from "../Common/ModalProfileHeaderWithoutImage";
import { capitalizeFirstLetter } from "../../helpers";
import Config from "../../Config";

// ---------- Helpers ----------
// Decode index-based bitmask -> array of task ids that were completed
const decodeChecklist = (bitmask, taskList) => {
    if (!bitmask || !taskList?.length) return [];
    return taskList
        .filter((_, index) => (bitmask & (1 << index)) !== 0)
        .map((t) => t.id);
};

// "Floor-1" / "Flat-A" based on propType
const getGroupLabel = (propType, flatFloorName) => {
    const group = propType === Config.PROPERTY_TYPE.PG ? "Floor" : "Flat";
    return `${group}-${flatFloorName ?? "-"}`;
};

const ViewDetails = ({
    setIsViewDetails,
    selectedData,
    taskList = [],
}) => {
    const onClose = () => setIsViewDetails(false);

    const task = selectedData?.tasks?.[0];
    const isCleaned = !!task;

    const completedIds = decodeChecklist(task?.taskCompleted, taskList);
    const hasChecklist = completedIds.length > 0;

    const images = task?.taskImages ?? [];
    const remarks = task?.remarks ?? "";

    const formatDateTime = (date) => {
        if (!date) return "-";
        return moment(date).format("DD, MMM YYYY, hh:mm A");
    };

    return (
        <Container>
            {/* Header */}
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    View Details
                </h1>
                <button onClick={onClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <ModalProfileHeaderWithoutImage
                title="Room Details"
                name={`Room ${selectedData?.roomNum ?? "-"}`}
                subtitle={`# ${getGroupLabel(
                    selectedData?.propType,
                    selectedData?.flatFloorName,
                )} · ${selectedData?.propName ?? "-"}`}
                // subtitle={`#${selectedData?.propName ?? "-"}`}
                onClose={onClose}
            />

            <ModalContent>
                <ScrollableContent>
                    <Wrapper>

                        {/* ---------- Cleaning Task (only when cleaned) ---------- */}
                        {isCleaned && (
                            <>
                                <BtnWrapper>
                                    <ModalSectionTitle>
                                        Cleaning Details
                                    </ModalSectionTitle>
                                </BtnWrapper>
                                <ModalSection>
                                    <ModalRow>
                                        <FlexCol>
                                            <ModalRowLabel>
                                                Done By
                                            </ModalRowLabel>
                                            <ModalRowValue>
                                                {task?.staffName
                                                    ? capitalizeFirstLetter(
                                                        task.staffName,
                                                    )
                                                    : "-"}
                                            </ModalRowValue>
                                        </FlexCol>
                                        <FlexCol>
                                            <ModalRowLabel>
                                                Staff Mobile
                                            </ModalRowLabel>
                                            <ModalRowValue>
                                                {task?.staffMobile || "-"}
                                            </ModalRowValue>
                                        </FlexCol>
                                    </ModalRow>

                                    <ModalRow>
                                        <FlexCol>
                                            <ModalRowLabel>
                                                Recorded By
                                            </ModalRowLabel>
                                            <ModalRowValue>
                                                {task?.recordedByName || "-"}
                                            </ModalRowValue>
                                        </FlexCol>
                                        <FlexCol>
                                            <ModalRowLabel>
                                                Task Date
                                            </ModalRowLabel>
                                            <ModalRowValue>
                                                {formatDateTime(
                                                    task?.taskDate,
                                                )}
                                            </ModalRowValue>
                                        </FlexCol>
                                    </ModalRow>
                                    {remarks && (
                                        <ModalRow className="grid-cols-1">
                                            <FlexCol>
                                                <ModalRowLabel>Remarks</ModalRowLabel>
                                                <ModalRowValue>{remarks}</ModalRowValue>
                                            </FlexCol>
                                        </ModalRow>
                                    )}
                                </ModalSection>

                                {/* Checklist */}
                                {hasChecklist && taskList?.length > 0 && (
                                    <>
                                        <BtnWrapper>
                                            <ModalSectionTitle>
                                                Cleaning Checklist
                                            </ModalSectionTitle>
                                        </BtnWrapper>
                                        <ModalSection>
                                            <ChecklistWrapper>
                                                {taskList.map((t) => {
                                                    const done =
                                                        completedIds.includes(
                                                            t.id,
                                                        );
                                                    return (
                                                        <ChecklistChip
                                                            key={t.id}
                                                            $done={done}
                                                        >
                                                            <span>
                                                                {done
                                                                    ? "✓"
                                                                    : "✕"}
                                                            </span>
                                                            <span>
                                                                {t.name}
                                                            </span>
                                                        </ChecklistChip>
                                                    );
                                                })}
                                            </ChecklistWrapper>
                                        </ModalSection>
                                    </>
                                )}

                                {/* Images */}
                                {images.length > 0 && (
                                    <>
                                        <BtnWrapper>
                                            <ModalSectionTitle>
                                                Images
                                            </ModalSectionTitle>
                                        </BtnWrapper>
                                        <ModalSection>
                                            <ImageGrid>
                                                {images.map((img, idx) => {
                                                    const uri =
                                                        typeof img === "string"
                                                            ? img
                                                            : img?.uri ??
                                                            img?.url ??
                                                            img?.value ??
                                                            "";
                                                    if (!uri) return null;
                                                    return (
                                                        <ImageThumb
                                                            key={`${uri}-${idx}`}
                                                            src={uri}
                                                            alt={`task-${idx + 1}`}
                                                            onClick={() => window.open(uri, "_blank", "noopener,noreferrer")}
                                                        />
                                                    );
                                                })}
                                            </ImageGrid>
                                        </ModalSection>
                                    </>
                                )}
                            </>
                        )}

                        {/* ---------- Not cleaned fallback ---------- */}
                        {!isCleaned && (
                            <ModalSection>
                                <p className="text-sm text-gray-500 py-4 text-center">
                                    No cleaning record found for this room.
                                </p>
                            </ModalSection>
                        )}
                    </Wrapper>
                </ScrollableContent>
            </ModalContent>
        </Container>
    );
};

export default ViewDetails;

// ---------------- Styled Components ----------------
const Wrapper = tw.div`grid gap-2 my-0`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const BtnWrapper = tw.div`w-full flex items-center justify-between`;
const FlexCol = tw.div`flex flex-col gap-0.5 min-w-0`;

const StatusBanner = tw.div`
    flex items-center justify-between
    rounded-lg px-4 py-3 mb-2
    ${(p) =>
        p.$cleaned
            ? "bg-green-50 text-green-700 border border-green-100"
            : "bg-rose-50 text-rose-700 border border-rose-100"}
`;

const ChecklistWrapper = tw.div`flex flex-wrap gap-3`;
const ChecklistChip = tw.span`
    inline-flex items-center gap-1
    rounded-full px-3 py-2 text-xs
    ${(p) =>
        p.$done
            ? "bg-green-100 text-green-700"
            : "bg-gray-200 text-gray-500"}
`;

const ImageGrid = tw.div`flex flex-wrap gap-2`;
const ImageThumb = tw.img`h-28 w-28 rounded-lg object-cover border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity`;